import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shield, CheckCircle, Clock, XCircle, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_CONFIG = {
  not_required: { label: "Não necessário", icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  pending: { label: "Verificação pendente", icon: AlertTriangle, color: "text-yellow-400", bg: "bg-yellow-500/10" },
  in_review: { label: "Em análise", icon: Clock, color: "text-blue-400", bg: "bg-blue-500/10" },
  approved: { label: "Aprovado", icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-500/10" },
  rejected: { label: "Recusado", icon: XCircle, color: "text-red-400", bg: "bg-red-500/10" },
};

const TRUSTIC_SCRIPT_URL = "https://api.trustic.com.br/antifraud/v1/embeddeds/onboarding/z_kyc_kredz/1d25af3f-1669-460a-9077-8d23a3917c75";

export default function KycBanner() {
  const [kycData, setKycData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get("/kyc/status");
      setKycData(res.data);
    } catch { toast.error("Erro ao verificar KYC"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleStartKyc = async () => {
    try {
      await api.post("/kyc/start");
      setShowModal(true);
    } catch (err) {
      if (err.response?.data?.whitelisted) {
        toast.success("Seu banco não requer verificação KYC");
        fetchStatus();
      } else {
        toast.error(err.response?.data?.detail || "Erro");
      }
    }
  };

  useEffect(() => {
    if (!showModal) return;
    const timer = setTimeout(() => {
      const container = document.getElementById("trustic-container");
      if (container && !container.querySelector("script")) {
        const script = document.createElement("script");
        script.id = "trustic-embedded-onboarding";
        script.src = TRUSTIC_SCRIPT_URL;
        script.async = true;
        container.appendChild(script);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [showModal]);

  if (loading || !kycData) return null;
  if (kycData.whitelisted && kycData.kyc_status === "not_required") return null;

  const config = STATUS_CONFIG[kycData.kyc_status] || STATUS_CONFIG.pending;
  const StatusIcon = config.icon;
  const needsAction = kycData.kyc_status === "pending" || kycData.kyc_status === "rejected";

  return (
    <>
      <Card className={`border-white/5 ${kycData.kyc_status === "rejected" ? "border-red-500/20" : ""}`} data-testid="kyc-banner">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${config.bg}`}>
                <StatusIcon className={`w-4 h-4 ${config.color}`} />
              </div>
              <div>
                <p className="text-sm font-semibold flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5" /> Verificação de Identidade (KYC)
                </p>
                <p className={`text-xs ${config.color}`}>{config.label}</p>
                {kycData.kyc_status === "in_review" && (
                  <p className="text-xs text-muted-foreground mt-0.5">Estamos analisando seus dados. Você será notificado.</p>
                )}
                {kycData.kyc_status === "rejected" && (
                  <p className="text-xs text-red-400 mt-0.5">Entre em contato com o suporte para mais informações.</p>
                )}
                {kycData.kyc_status === "pending" && (
                  <p className="text-xs text-muted-foreground mt-0.5">Necessário para liberar antecipações com este banco.</p>
                )}
              </div>
            </div>
            {needsAction && (
              <Button data-testid="start-kyc-btn" onClick={handleStartKyc} size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                {kycData.kyc_status === "rejected" ? "Tentar Novamente" : "Iniciar Verificação"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-2xl border-white/10 max-h-[90vh] overflow-y-auto" data-testid="kyc-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-400" /> Verificação de Identidade
            </DialogTitle>
            <DialogDescription>Complete o processo abaixo para liberar suas antecipações</DialogDescription>
          </DialogHeader>
          <div id="trustic-container" className="min-h-[400px] rounded-lg bg-white p-2">
            <div className="flex items-center justify-center h-[400px] text-muted-foreground">
              <div className="loading-spinner w-6 h-6" />
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center">Após concluir, feche esta janela. O resultado será processado automaticamente.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
